import { AlertTriangle, HelpCircle } from 'lucide-react';
import { cn } from '../ui/utils';
import { bandStyle, gradeState, GRADE_STATE_LABEL, type OrganScore } from './organHealth';

/**
 * The single source of truth for how an organ's grade is chipped. Three visual
 * states, never folded (§ missing-data: empty ≠ zero ≠ not-gradeable):
 *   - scored       → tinted band accent + severity glyph + label.
 *   - error        → outlined destructive, no fill.
 *   - no_data /    → dashed neutral chip, NO colour patch: there's no severity
 *     not_gradeable   to encode, so borrowing a band colour would lie.
 * Severity is never colour-only — every state carries an icon + word.
 */
export function BandChip({ organ, size = 'sm' }: { organ: OrganScore; size?: 'sm' | 'md' }) {
  const style = bandStyle(organ.band);
  const Icon = style.icon;
  const state = gradeState(organ);
  const pad = size === 'md' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs';
  const iconSize = size === 'md' ? 'size-4' : 'size-3';

  if (state === 'scored') {
    return (
      <span className={cn('inline-flex items-center gap-1.5 rounded-sm border border-transparent font-medium', pad, style.accent)}>
        <Icon className={iconSize} aria-hidden="true" />
        {style.glyph ? <span aria-hidden="true">{style.glyph}</span> : null}
        {style.label}
      </span>
    );
  }

  if (state === 'error') {
    return (
      <span className={cn('inline-flex items-center gap-1.5 rounded-sm border border-destructive/50 font-medium text-destructive-text', pad)}>
        <AlertTriangle className={iconSize} aria-hidden="true" />
        {GRADE_STATE_LABEL.error}
      </span>
    );
  }

  return (
    <span
      className={cn('inline-flex items-center gap-1.5 rounded-sm border border-dashed border-border font-medium text-text-tertiary', pad)}
      title="Function = how the organ itself is doing. It needs this organ's anchoring test (the card names it), which is not on file. A 'Modifiable risk' number below is the SEPARATE risk-factor track — it does not mean the organ was graded."
    >
      <HelpCircle className={iconSize} aria-hidden="true" />
      {GRADE_STATE_LABEL[state]}
    </span>
  );
}
